import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { serversApi, UsageLogsResponse } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface LogsTabProps {
  serverId: string
  serverName?: string
}

export function LogsTab({ serverId, serverName }: LogsTabProps) {
  const [toolFilter, setToolFilter] = useState('')
  const [transportFilter, setTransportFilter] = useState('all')
  const [errorsOnly, setErrorsOnly] = useState(false)
  const [limit, setLimit] = useState(100)

  const queryParams = useMemo(() => ({
    limit,
    tool_name: toolFilter || undefined,
    errors_only: errorsOnly || undefined,
    transport: transportFilter !== 'all' ? transportFilter : undefined,
  }), [limit, toolFilter, errorsOnly, transportFilter])

  const { data, isLoading, isError, refetch, isFetching } = useQuery<UsageLogsResponse>({
    queryKey: ['serverUsageLogs', serverId, queryParams],
    queryFn: async () => serversApi.getUsageLogs(serverId, queryParams),
    enabled: !!serverId,
    refetchInterval: 10000,
  })

  useEffect(() => {
    // refetch on filter change implicitly via queryKey
  }, [queryParams])

  const entries = data?.entries || []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>MCP Usage Logs</CardTitle>
          <CardDescription>
            Connection and tool request/response logs for {serverName || serverId}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3">
            <p className="text-xs text-muted-foreground">
              ℹ️ Monitoring Information — Logs refresh automatically every 10 seconds to provide near real-time visibility while minimizing API usage.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filter by tool name"
                value={toolFilter}
                onChange={(e) => setToolFilter(e.target.value)}
                className="w-56"
              />
            </div>
            <div className="flex items-center gap-2">
              <Select value={transportFilter} onValueChange={setTransportFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Transport" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All transports</SelectItem>
                  <SelectItem value="http">HTTP</SelectItem>
                  <SelectItem value="shttp">SHTTP</SelectItem>
                  <SelectItem value="websocket">WebSocket</SelectItem>
                  <SelectItem value="sse">SSE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant={errorsOnly ? 'destructive' : 'outline'} size="sm" onClick={() => setErrorsOnly((v) => !v)}>
                {errorsOnly ? 'Errors only' : 'All statuses'}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v))}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Limit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="250">250</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={() => refetch()} disabled={isFetching}>Refresh</Button>
          </div>

          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading logs...</div>
          ) : isError ? (
            <div className="text-sm text-red-600">Failed to load logs.</div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-muted-foreground">No logs found for the selected filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2 px-3">Time</th>
                    <th className="py-2 px-3">Method</th>
                    <th className="py-2 px-3">Tool</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Latency</th>
                    <th className="py-2 px-3">Transport</th>
                    <th className="py-2 px-3">Client</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="py-2 px-3 font-mono">
                        {new Date(e.timestamp).toLocaleString()}
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline">{e.method}</Badge>
                      </td>
                      <td className="py-2 px-3">{e.tool_name || '-'}</td>
                      <td className="py-2 px-3">
                        <Badge variant={e.status_code && e.status_code >= 400 ? 'destructive' : 'default'}>
                          {e.status_code ?? '-'}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">{typeof e.response_time === 'number' ? `${(e.response_time/1000000).toFixed(3)}.ms` : '-'}</td>
                      <td className="py-2 px-3">{e.transport || '-'}</td>
                      <td className="py-2 px-3">
                        <span title={e.user_agent || ''}>{e.client_ip || '-'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
